import {
  DeleteOutlineOutlined,
  ModeEditOutlineOutlined,
} from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Seller, SellerBrandSettings } from 'shared/types/Sellers';
import { ConfirmationModal } from 'shared/ui/ConfirmationModal';
import { EditBrandInfoModal } from 'shared/ui/EditBrandInfoModal';
import { EditBrandLogoModal } from 'shared/ui/EditBrandLogoModal';

interface BrandInfoFormProps {
  seller: Seller;
  brandColor: string | null;
  brandLogo: string | null;
  isUpdatingBrand: boolean;
  isPermittedToModifySeller: boolean;
  handleUpdateBrand: (brandSettings: SellerBrandSettings) => void;
  uploadSellerLogo: (file: File) => void;
  deleteSellerLogo: () => void;
}

const BrandInfoForm = ({
  seller,
  brandColor,
  brandLogo,
  isUpdatingBrand,
  isPermittedToModifySeller,
  handleUpdateBrand,
  uploadSellerLogo,
  deleteSellerLogo,
}: BrandInfoFormProps) => {
  const [isInEditBrandColor, setIsInEditBrandColor] = useState(false);
  const [isInEditLogo, setIsInEditLogo] = useState(false);
  const [isInDeleteLogo, setIsInDeleteLogo] = useState(false);
  const { t } = useTranslation();

  const handleChangeBrandColor = async (value: string) => {
    value = value.toUpperCase().replace('#', '');
    await handleUpdateBrand({
      brandColor: value,
      logo: brandLogo,
    });
    setIsInEditBrandColor(false);
  };

  const handleChangeLogo = async (file: File) => {
    await uploadSellerLogo(file);
    setIsInEditLogo(false);
  };

  const handleDeleteLogo = async () => {
    await deleteSellerLogo();
    setIsInDeleteLogo(false);
  };

  return (
    <>
      {/* Brand Color */}

      <Box
        sx={{
          marginTop: '24px',
          '&:hover .editIconButton': {
            visibility: isPermittedToModifySeller ? 'visible' : 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            position: 'relative',
            width: '100%',
          }}
        >
          <Typography mt="4px" typography="openSans.h4">
            {t('Brand Color')}
          </Typography>
          <IconButton
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              visibility: 'hidden',
            }}
            className="editIconButton"
            onClick={() => setIsInEditBrandColor(true)}
          >
            <ModeEditOutlineOutlined color="primary" />
          </IconButton>
        </Box>
        <Box
          mt={2}
          sx={{
            borderBottom: '1px solid #00000020',
            paddingBottom: '8px',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '16px',
              height: '16px',
              backgroundColor: brandColor,
              borderRadius: '4px',
              border: '1px solid rgba(50, 50, 93, 0.05)',
              boxShadow: 'rgba(50, 50, 93, 0.11) 0px 4px 6px 0px',
            }}
          ></Box>
          <Typography
            sx={{ lineHeight: '26px' }}
            typography="openSans.subtitle1"
            color="#00000080"
          >
            {brandColor}
          </Typography>
        </Box>
        {isInEditBrandColor && (
          <EditBrandInfoModal
            isModalOpen={isInEditBrandColor}
            handleCloseModal={() => setIsInEditBrandColor(false)}
            approveNameButton={t('Save')}
            selectedBrandColor={brandColor}
            isUpdatingBrand={isUpdatingBrand}
            handleChangeBrandColor={handleChangeBrandColor}
          />
        )}
      </Box>

      {/* Logo */}

      <Box
        sx={{
          marginTop: '24px',
          '&:hover .editIconButton, &:hover .deleteIconButton': {
            visibility: isPermittedToModifySeller ? 'visible' : 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            position: 'relative',
            width: '100%',
          }}
        >
          <Typography mt="4px" typography="openSans.h4">
            {t('Brand Logo')}
          </Typography>
          <IconButton
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              visibility: 'hidden',
            }}
            className="editIconButton"
            onClick={() => setIsInEditLogo(true)}
          >
            <ModeEditOutlineOutlined color="primary" />
          </IconButton>
          {brandLogo && (
            <IconButton
              sx={{
                position: 'absolute',
                right: '50px',
                top: '50%',
                transform: 'translateY(-50%)',
                visibility: 'hidden',
              }}
              className="deleteIconButton"
              onClick={() => setIsInDeleteLogo(true)}
            >
              <DeleteOutlineOutlined color="primary" />
            </IconButton>
          )}
        </Box>
        <Box
          mt={2}
          sx={{
            borderBottom: '1px solid #00000020',
            paddingBottom: '8px',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          {brandLogo ? (
            <img
              src={brandLogo}
              alt="brand logo"
              style={{ width: '64px', height: '64px' }}
            />
          ) : (
            <Typography
              mt="4px"
              typography="openSans.subtitle1"
              color="#00000080"
            >
              {t('No logo provided')}
            </Typography>
          )}
        </Box>

        {isInDeleteLogo && (
          <ConfirmationModal
            title={t('Delete Brand Logo')}
            description={t('Are you sure you want to delete your brand logo?')}
            handleApproveModal={handleDeleteLogo}
            handleCloseModal={() => setIsInDeleteLogo(false)}
            isModalOpen={isInDeleteLogo}
            isSaving={isUpdatingBrand}
            approveNameButton={t('Delete')}
            approveButtonColor="error"
          />
        )}

        {isInEditLogo && (
          <EditBrandLogoModal
            titleModal={t('Brand Logo')}
            isModalOpen={isInEditLogo}
            handleCloseModal={() => setIsInEditLogo(false)}
            approveNameButton={t('Save')}
            isUpdatingBrand={isUpdatingBrand}
            handleChangeLogo={handleChangeLogo}
            seller={seller}
          />
        )}
      </Box>
    </>
  );
};

export { BrandInfoForm };
